import { useEffect, useRef, useContext, useState, useMemo } from 'react';
import { io } from 'socket.io-client';
import { UserContext } from '../user/UserContext';
import { API_URL } from '../../config';
import { SocketContext } from './SocketContext';

export default function SocketProvider({ children }) {
  const { loggedIn, user } = useContext(UserContext);
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    if (loggedIn && user?.id) {
      // Create single socket instance
      const newSocket = io(API_URL, {
        withCredentials: true,
        transports: ['polling', 'websocket'], // Polling first for better compatibility and to ensure headers are sent
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        extraHeaders: {
          'ngrok-skip-browser-warning': 'true',
        },
        transportOptions: {
          polling: {
            extraHeaders: {
              'ngrok-skip-browser-warning': 'true',
            },
          },
        },
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      // Re-initialize state on reconnection
      newSocket.on('connect', () => {
        console.log('Socket connected:', newSocket.id);
        newSocket.emit('user-online', user.id);
      });

      newSocket.on('disconnect', (reason) => {
        console.warn('Socket disconnected:', reason);
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      return () => {
        console.log('Cleaning up socket connection');
        newSocket.disconnect();
        socketRef.current = null;
        setSocket(null);
      };
    } else {
      // If not logged in, ensure socket is disconnected
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    }
  }, [loggedIn, user?.id]);

  const value = useMemo(() => ({ socket }), [socket]);

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
}
